import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useDrop } from 'react-dnd'
import DroppableChipSlot from './DroppableChipSlot'
import { ItemType } from './DraggableUserChip'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import i18n from 'i18next'
import { ColorBaseSkyBlue } from 'assets/styles/variables'
import { AttendeeData } from './ThreeSectionAttendeeManager/attendeeUtils'

export interface DragItem {
    type: string
    id: string | number
    external_id?: string | number | null
    index: number
    sourceSection?: string
    name: string
    text?: string
    email: string | null
    isTimeLate?: boolean
    isLocationRemote?: boolean
}

type Props = {
    attendees: AttendeeData[]
    onMove: (dragIndex: number, hoverIndex: number) => void
    onCrossSectionMove?: (
        attendee: AttendeeData,
        targetSection: string,
        dropPosition?: number
    ) => void
    onRemove?: (index: number) => void
    onAdd?: (nameOrAttendee: string | AttendeeData) => void
    targetSection?: string
    icon?: React.ReactNode
    interactiveIcon?: React.ReactNode
    secondaryInteractiveIcon?: React.ReactNode
    searchOptions?: AttendeeData[]
    enableSearch?: boolean
    crossSectionAttendees?: AttendeeData[]
    onMoveToNextSection?: (attendee: AttendeeData) => void
    onMoveToPreviousSection?: (attendee: AttendeeData) => void
    onTimeIconToggle?: (attendeeId: string | number) => void
    onLocationIconToggle?: (attendeeId: string | number) => void
    placeholder?: string
    readonlyForCreate?: boolean
    dataAnalytics?: string
}

const Container = styled.div<{ isOver: boolean; canDrop: boolean }>`
    border: 1px solid #949494;
    border-radius: 4px;
    margin-top: 6px;
    min-height: 70px;
    line-height: 20px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    background-color: transparent;
    transition: all 0.2s ease;
    position: relative;
    padding-right: 6px;

    &:focus-within {
        border: 1px solid #1e1e1e;
        box-shadow: 0px 0px 0px 2px ${ColorBaseSkyBlue};
    }
`

const InlineInput = styled.input`
    border: none;
    outline: none;
    background: transparent;
    font-size: 16px;
    padding: 4px 12px 8px 12px;
    margin: 4px 0 0 4px;
    min-width: 120px;
    flex: 1;
    font-family: 'Source Sans Pro';
    font-weight: 400;
    line-height: 20px;

    &::placeholder {
        color: rgba(0, 0, 0, 0.38);
        font-family: 'Source Sans Pro';
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        opacity: 1;
    }

    &:focus {
        background: transparent;
        outline: none;
    }
`

const SearchDropdown = styled.div<{ isVisible: boolean }>`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ccc;
    border-top: none;
    border-radius: 0 0 4px 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    display: ${(props) => (props.isVisible ? 'block' : 'none')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SearchDropdownItem = styled.div<{
    isHighlighted?: boolean
    isAddNew?: boolean
    isFirst?: boolean
}>`
    padding: 8px 12px;
    cursor: pointer;
    background-color: ${(props) => {
        if (props.isHighlighted) return 'rgb(235, 238, 242)'
        return 'white'
    }};

    &:hover {
        background-color: ${(props) => (props.isAddNew ? 'white' : 'rgb(235, 238, 242)')};
    }
`

const SearchDropdownItemText = styled.div`
    font-size: 14px;
    color: #333;
`

const SearchDropdownItemSubtext = styled.div`
    font-size: 12px;
    color: #666;
    margin-top: 2px;
`

const GapDropIndicator = styled.div<{ left: number; top: number }>`
    position: absolute;
    left: ${(props) => props.left}px;
    top: ${(props) => props.top}px;
    width: 2px;
    height: 28px;
    background: #1e1e1e;
    border-radius: 1px;
    pointer-events: none;
    z-index: 10;
`

const DuplicateMessage = styled(SearchDropdownItem)`
    cursor: default;
    opacity: 0.7;
`

const DuplicateMessageText = styled(SearchDropdownItemText)`
    font-style: italic;
    color: #666;
`

const inputVisibilityStyle = (shouldShow: boolean) => css`
    width: ${shouldShow ? 'auto' : '1px'};
    height: ${shouldShow ? 'auto' : '1px'};
    opacity: ${shouldShow ? 1 : 0};
    padding: ${shouldShow ? undefined : 0};
    margin: ${shouldShow ? undefined : 0};
    border: ${shouldShow ? undefined : 'none'};
    position: ${shouldShow ? 'relative' : 'absolute'};
`

export const DraggableChipContainer: React.FC<Props> = ({
    attendees,
    onMove,
    onCrossSectionMove,
    onRemove,
    onAdd,
    targetSection,
    icon,
    interactiveIcon,
    secondaryInteractiveIcon,
    searchOptions = [],
    enableSearch = false,
    crossSectionAttendees = [],
    onMoveToNextSection,
    onMoveToPreviousSection,
    onTimeIconToggle,
    onLocationIconToggle,
    placeholder,
    readonlyForCreate = false,
    dataAnalytics
}) => {
    const [inputValue, setInputValue] = useState('')
    const [isInputVisible, setIsInputVisible] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const highlightedItemRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const isInteractingWithChipRef = useRef<boolean>(false)
    const isDraggingRef = useRef<boolean>(false)

    useEffect(() => {
        if (highlightedItemRef.current) {
            highlightedItemRef.current.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            })
        }
    }, [highlightedIndex])

    useEffect(() => {
        if (isInputVisible && inputRef.current) {
            inputRef.current.focus({ preventScroll: true })
        }
    }, [isInputVisible])

    const isValidAttendee = useCallback(
        (attendee: AttendeeData | null | undefined): attendee is AttendeeData => {
            return !!(attendee && (attendee.id || attendee.text || attendee.name))
        },
        []
    )

    const validAttendees = useMemo(() => attendees.filter(isValidAttendee), [
        attendees,
        isValidAttendee
    ])

    const allAttendeesForValidation = useMemo(
        () => [...validAttendees, ...crossSectionAttendees.filter(isValidAttendee)],
        [validAttendees, crossSectionAttendees, isValidAttendee]
    )

    const filteredSearchOptions = useMemo(() => {
        if (!enableSearch || !inputValue) return searchOptions

        const searchText = inputValue.toLowerCase()
        return searchOptions.filter(
            (option) =>
                option.text?.toLowerCase().includes(searchText) ||
                option.name?.toLowerCase().includes(searchText) ||
                option.email?.toLowerCase().includes(searchText)
        )
    }, [enableSearch, searchOptions, inputValue])

    const createAttendeeData = useCallback((item: DragItem): AttendeeData => {
        const attendeeData = {
            id: item.id || `fallback_${item.name || item.text || 'unknown'}`,
            external_id: item.external_id,
            text: item.name || item.text,
            name: item.name || item.text,
            email: item.email,
            value: item.name || item.text,
            isTimeLate: item.isTimeLate,
            isLocationRemote: item.isLocationRemote
        }
        return attendeeData
    }, [])

    const [gapDropIndex, setGapDropIndex] = useState<number | null>(null)
    const [gapIndicatorPosition, setGapIndicatorPosition] = useState<{
        left: number
        top: number
    } | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)

    const calculateGapIndex = useCallback(
        (
            clientX: number,
            clientY: number
        ): { index: number | null; chipSlots: NodeListOf<Element> | null } => {
            if (!containerRef.current) return { index: null, chipSlots: null }

            const chipSlots = containerRef.current.querySelectorAll('[data-chip-slot]')
            if (chipSlots.length === 0) return { index: validAttendees.length, chipSlots }

            // Get all chip positions
            const chipPositions: {
                left: number
                right: number
                centerX: number
                top: number
                bottom: number
                index: number
            }[] = []
            chipSlots.forEach((slot, index) => {
                const rect = slot.getBoundingClientRect()
                chipPositions.push({
                    left: rect.left,
                    right: rect.right,
                    centerX: rect.left + rect.width / 2,
                    top: rect.top,
                    bottom: rect.bottom,
                    index
                })
            })

            const mouseRow = chipPositions.filter(
                (pos) => clientY >= pos.top && clientY <= pos.bottom
            )

            if (mouseRow.length === 0) {
                return { index: validAttendees.length, chipSlots }
            }

            mouseRow.sort((a, b) => a.left - b.left)

            // Check if mouse is before the first chip in the row
            if (clientX < mouseRow[0].centerX) {
                return { index: mouseRow[0].index, chipSlots }
            }

            // Check if mouse is after the last chip in the row
            if (clientX > mouseRow[mouseRow.length - 1].centerX) {
                return { index: mouseRow[mouseRow.length - 1].index + 1, chipSlots }
            }

            // Find which gap the mouse is in
            for (let i = 0; i < mouseRow.length - 1; i++) {
                const current = mouseRow[i]
                const next = mouseRow[i + 1]
                const gapCenterX = (current.right + next.left) / 2

                if (clientX >= current.centerX && clientX < next.centerX) {
                    // Mouse is between current and next chip
                    if (clientX < gapCenterX) {
                        return { index: current.index + 1, chipSlots }
                    } else {
                        return { index: next.index, chipSlots }
                    }
                }
            }

            return { index: validAttendees.length, chipSlots }
        },
        [validAttendees.length]
    )

    const updateGapIndicator = useCallback(
        (gapIndex: number | null, chipSlots: NodeListOf<Element> | null) => {
            if (gapIndex === null || !containerRef.current || !chipSlots) {
                setGapIndicatorPosition(null)
                return
            }

            const containerRect = containerRef.current.getBoundingClientRect()

            if (chipSlots.length === 0) {
                setGapIndicatorPosition(null)
                return
            }

            let left: number
            let top: number
            const indicatorHeight = 30

            if (gapIndex === 0) {
                // Before first chip
                const firstSlot = chipSlots[0]
                const rect = firstSlot.getBoundingClientRect()
                left = rect.left - containerRect.left - 10
                // Center vertically relative to chip
                const chipCenterY = rect.top + rect.height / 2 - containerRect.top
                top = chipCenterY - indicatorHeight / 2
            } else if (gapIndex >= chipSlots.length) {
                // After last chip
                const lastSlot = chipSlots[chipSlots.length - 1]
                const rect = lastSlot.getBoundingClientRect()
                left = rect.right - containerRect.left + 6
                // Center vertically relative to chip
                const chipCenterY = rect.top + rect.height / 2 - containerRect.top
                top = chipCenterY - indicatorHeight / 2
            } else {
                // Between chips
                const prevSlot = chipSlots[gapIndex - 1]
                const nextSlot = chipSlots[gapIndex]
                const prevRect = prevSlot.getBoundingClientRect()
                const nextRect = nextSlot.getBoundingClientRect()

                // Check if they're on the same row
                if (Math.abs(prevRect.top - nextRect.top) < 10) {
                    // Center in gap, offset by 1px for indicator width (2px)
                    left = (prevRect.right + nextRect.left) / 2 - containerRect.left - 2
                    // Center vertically relative to chips
                    const chipCenterY = prevRect.top + prevRect.height / 2 - containerRect.top
                    top = chipCenterY - indicatorHeight / 2
                } else {
                    // Different rows - show at start of next chip's row
                    left = nextRect.left - containerRect.left - 10
                    // Center vertically relative to next chip
                    const chipCenterY = nextRect.top + nextRect.height / 2 - containerRect.top
                    top = chipCenterY - indicatorHeight / 2
                }
            }

            setGapIndicatorPosition({ left, top })
        },
        []
    )

    const [{ isOver, canDrop }, drop] = useDrop<
        DragItem,
        void,
        { isOver: boolean; canDrop: boolean }
    >({
        accept: ItemType,
        hover: (_item: DragItem, monitor) => {
            const clientOffset = monitor.getClientOffset()
            if (clientOffset && monitor.isOver({ shallow: true })) {
                const { index: newGapIndex, chipSlots } = calculateGapIndex(
                    clientOffset.x,
                    clientOffset.y
                )
                if (newGapIndex !== gapDropIndex) {
                    setGapDropIndex(newGapIndex)
                    updateGapIndicator(newGapIndex, chipSlots)
                }
            }
        },
        drop: (item: DragItem, monitor) => {
            if (!monitor.didDrop()) {
                const sourceSection = item.sourceSection

                if (sourceSection && targetSection && sourceSection !== targetSection) {
                    if (onCrossSectionMove) {
                        const dropPosition = gapDropIndex !== null ? gapDropIndex : undefined
                        onCrossSectionMove(createAttendeeData(item), targetSection, dropPosition)
                    }
                } else {
                    const dragIndex = item.index
                    let hoverIndex = gapDropIndex !== null ? gapDropIndex : validAttendees.length

                    // Adjust index if dragging from before the drop position
                    if (dragIndex < hoverIndex) {
                        hoverIndex = hoverIndex - 1
                    }

                    if (dragIndex !== hoverIndex && onMove) {
                        onMove(dragIndex, hoverIndex)
                    }
                }
            }
            setGapDropIndex(null)
            setGapIndicatorPosition(null)
        },
        collect: (monitor) => {
            const isOverCurrent = monitor.isOver({ shallow: true })
            if (!isOverCurrent) {
                setGapDropIndex(null)
                setGapIndicatorPosition(null)
            }
            return {
                isOver: isOverCurrent,
                canDrop: monitor.canDrop()
            }
        }
    })

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value
            setInputValue(newValue)
            setIsInputVisible(true)
            if (enableSearch) {
                setShowDropdown(newValue.length > 0)
                setHighlightedIndex(newValue.length > 0 ? 0 : -1)
            }
        },
        [enableSearch]
    )

    const isDuplicate = useCallback(
        (name: string) => {
            try {
                if (!name || !name.trim()) return false

                const normalizedName = name.toLowerCase().trim().replace(/\s+/g, ' ')
                const isDup = allAttendeesForValidation.some((attendee) => {
                    const attendeeName = (attendee.text || attendee.name)
                        ?.toLowerCase()
                        ?.trim()
                        .replace(/\s+/g, ' ')
                    return attendeeName === normalizedName
                })

                return isDup
            } catch (error) {
                return false
            }
        },
        [allAttendeesForValidation]
    )

    const hasCustomOption = useMemo(() => {
        const trimmedValue = inputValue.trim()
        const normalizedInput = trimmedValue.toLowerCase().replace(/\s+/g, ' ')
        return (
            trimmedValue &&
            !filteredSearchOptions.some(
                (opt) =>
                    (opt.text || opt.name)?.toLowerCase().replace(/\s+/g, ' ') === normalizedInput
            )
        )
    }, [inputValue, filteredSearchOptions])

    const isDuplicateCustom = useMemo(() => hasCustomOption && isDuplicate(inputValue.trim()), [
        hasCustomOption,
        isDuplicate,
        inputValue
    ])

    const totalSelectableItems = useMemo(
        () => filteredSearchOptions.length + (hasCustomOption && !isDuplicateCustom ? 1 : 0),
        [filteredSearchOptions.length, hasCustomOption, isDuplicateCustom]
    )

    const handleSearchOptionSelect = useCallback(
        (option: AttendeeData) => {
            try {
                const name = option.text || option.name || ''

                if (isDuplicate(name)) {
                    return
                }

                if (onAdd) {
                    onAdd(option)
                    setInputValue('')
                    setIsInputVisible(true)
                    setShowDropdown(false)
                    setHighlightedIndex(-1)
                }
            } catch (error) {
                // Silently handle error
            }
        },
        [onAdd, isDuplicate]
    )

    const handleCustomAdd = useCallback(
        (text: string) => {
            try {
                if (!text || !text.trim()) {
                    return
                }

                if (isDuplicate(text)) {
                    return
                }

                if (onAdd) {
                    onAdd(text)
                    setInputValue('')
                    setIsInputVisible(true)
                    setShowDropdown(false)
                    setHighlightedIndex(-1)
                }
            } catch (error) {
                // Silently handle error
            }
        },
        [onAdd, isDuplicate]
    )

    const handleRemove = useCallback(
        (index: number) => {
            if (onRemove) {
                isInteractingWithChipRef.current = true
                onRemove(index)
                setIsInputVisible(true)

                requestAnimationFrame(() => {
                    setTimeout(() => {
                        inputRef.current?.focus({ preventScroll: true })
                        isInteractingWithChipRef.current = false
                    }, 50)
                })
            }
        },
        [onRemove]
    )

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Backspace' && inputValue === '' && validAttendees.length > 0) {
                e.preventDefault()
                handleRemove(validAttendees.length - 1)
                return
            }

            if (enableSearch && showDropdown) {
                switch (e.key) {
                    case 'ArrowDown':
                        e.preventDefault()
                        setHighlightedIndex((prev) =>
                            prev < totalSelectableItems - 1 ? prev + 1 : 0
                        )
                        break
                    case 'ArrowUp':
                        e.preventDefault()
                        setHighlightedIndex((prev) =>
                            prev > 0 ? prev - 1 : totalSelectableItems - 1
                        )
                        break
                    case 'Enter':
                        e.preventDefault()
                        if (
                            highlightedIndex >= 0 &&
                            highlightedIndex < filteredSearchOptions.length
                        ) {
                            handleSearchOptionSelect(filteredSearchOptions[highlightedIndex])
                        } else if (
                            highlightedIndex === filteredSearchOptions.length &&
                            hasCustomOption &&
                            !isDuplicateCustom
                        ) {
                            handleCustomAdd(inputValue.trim())
                        } else if (highlightedIndex === -1 && filteredSearchOptions.length > 0) {
                            handleSearchOptionSelect(filteredSearchOptions[0])
                        }
                        break
                    case 'Escape':
                        setShowDropdown(false)
                        setHighlightedIndex(-1)
                        break
                }
            } else {
                if (e.key === 'Enter') {
                    const name = inputValue.trim()
                    if (name && onAdd && !isDuplicate(name)) {
                        onAdd(name)
                        setInputValue('')
                        setIsInputVisible(true)
                    }
                } else if (e.key === 'Escape') {
                    setInputValue('')
                    if (validAttendees.length > 0) setIsInputVisible(false)
                }
            }
        },
        [
            inputValue,
            validAttendees.length,
            handleRemove,
            enableSearch,
            showDropdown,
            totalSelectableItems,
            filteredSearchOptions,
            highlightedIndex,
            handleSearchOptionSelect,
            hasCustomOption,
            isDuplicateCustom,
            handleCustomAdd,
            onAdd,
            isDuplicate
        ]
    )

    const handleContainerClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (readonlyForCreate) return

            const target = e.target as HTMLElement
            const isClickOnChip = target.closest('[data-chip-slot]') !== null

            if (!isInputVisible && onAdd && !isClickOnChip) {
                setIsInputVisible(true)
            }
        },
        [readonlyForCreate, isInputVisible, onAdd]
    )

    const handleInputBlur = useCallback(() => {
        setTimeout(() => {
            if (isInteractingWithChipRef.current) return

            setInputValue('')
            if (validAttendees.length > 0) setIsInputVisible(false)
            setShowDropdown(false)
            setHighlightedIndex(-1)
        }, 150)
    }, [validAttendees.length])

    const handleChipClick = useCallback(() => {
        if (readonlyForCreate || isDraggingRef.current) {
            isDraggingRef.current = false
            return
        }

        isInteractingWithChipRef.current = true
        setIsInputVisible(true)

        requestAnimationFrame(() => {
            inputRef.current?.focus({ preventScroll: true })
            isInteractingWithChipRef.current = false
        })
    }, [readonlyForCreate])

    const handleFocusInput = useCallback(() => {
        if (readonlyForCreate) return

        isInteractingWithChipRef.current = true
        setIsInputVisible(true)

        requestAnimationFrame(() => {
            inputRef.current?.focus({ preventScroll: true })
            isInteractingWithChipRef.current = false
        })
    }, [readonlyForCreate])

    const handleChipMouseDown = useCallback(() => {
        isDraggingRef.current = false
        isInteractingWithChipRef.current = true
    }, [])

    const handleChipDragStart = useCallback(() => {
        isDraggingRef.current = true
        isInteractingWithChipRef.current = false
    }, [])

    const shouldShowInput = !readonlyForCreate && (validAttendees.length === 0 || isInputVisible)

    const combinedRef = useCallback(
        (node: HTMLDivElement | null) => {
            containerRef.current = node
            drop(node)
        },
        [drop]
    )

    return (
        <Container
            ref={combinedRef}
            onClick={handleContainerClick}
            isOver={isOver}
            canDrop={canDrop}>
            {validAttendees.map((attendee, index) => (
                <div
                    key={`${attendee.id || attendee.text || attendee.name || index}-${index}`}
                    onClick={readonlyForCreate ? undefined : handleChipClick}
                    onMouseDown={readonlyForCreate ? undefined : handleChipMouseDown}
                    onDragStart={readonlyForCreate ? undefined : handleChipDragStart}>
                    <DroppableChipSlot
                        attendee={attendee}
                        index={index}
                        onMove={onMove}
                        onCrossSectionMove={onCrossSectionMove}
                        onRemove={handleRemove}
                        targetSection={targetSection}
                        icon={icon}
                        interactiveIcon={interactiveIcon}
                        secondaryInteractiveIcon={secondaryInteractiveIcon}
                        totalChips={validAttendees.length}
                        onMoveToNextSection={onMoveToNextSection}
                        onMoveToPreviousSection={onMoveToPreviousSection}
                        isTimeLate={attendee.isTimeLate}
                        isLocationRemote={attendee.isLocationRemote}
                        onTimeIconToggle={() => onTimeIconToggle?.(attendee.id)}
                        onLocationIconToggle={() => onLocationIconToggle?.(attendee.id)}
                        onFocusInput={handleFocusInput}
                        readonlyForCreate={readonlyForCreate}
                    />
                </div>
            ))}
            {!readonlyForCreate && (
                <InlineInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    onFocus={() => setIsInputVisible(true)}
                    placeholder={validAttendees.length === 0 ? placeholder : ''}
                    tabIndex={0}
                    css={inputVisibilityStyle(shouldShowInput)}
                    data-analytics={dataAnalytics}
                />
            )}
            {gapIndicatorPosition && (
                <GapDropIndicator left={gapIndicatorPosition.left} top={gapIndicatorPosition.top} />
            )}

            {enableSearch && (
                <SearchDropdown isVisible={showDropdown}>
                    {filteredSearchOptions.map((option, index) => (
                        <SearchDropdownItem
                            key={option.id || index}
                            ref={index === highlightedIndex ? highlightedItemRef : null}
                            isHighlighted={index === highlightedIndex}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                handleSearchOptionSelect(option)
                            }}>
                            <SearchDropdownItemText>
                                {option.text || option.name}
                            </SearchDropdownItemText>
                            {option.email && (
                                <SearchDropdownItemSubtext>
                                    {option.email}
                                </SearchDropdownItemSubtext>
                            )}
                        </SearchDropdownItem>
                    ))}
                    {inputValue.trim() &&
                        !filteredSearchOptions.some(
                            (opt) =>
                                (opt.text || opt.name)?.toLowerCase().replace(/\s+/g, ' ') ===
                                inputValue.toLowerCase().trim().replace(/\s+/g, ' ')
                        ) &&
                        (isDuplicate(inputValue.trim()) ? (
                            <DuplicateMessage isHighlighted={false}>
                                <DuplicateMessageText>
                                    "{inputValue.trim()}" - {i18n.t('PREVIOUSLY_ADDED')}
                                </DuplicateMessageText>
                            </DuplicateMessage>
                        ) : (
                            <SearchDropdownItem
                                ref={
                                    highlightedIndex === filteredSearchOptions.length
                                        ? highlightedItemRef
                                        : null
                                }
                                isHighlighted={highlightedIndex === filteredSearchOptions.length}
                                isAddNew={true}
                                onMouseDown={(e) => {
                                    e.preventDefault()
                                    handleCustomAdd(inputValue.trim())
                                }}>
                                <SearchDropdownItemText>
                                    {i18n.t('CREATE')} "{inputValue.trim()}"
                                </SearchDropdownItemText>
                            </SearchDropdownItem>
                        ))}
                </SearchDropdown>
            )}
        </Container>
    )
}

export default DraggableChipContainer
